import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Add, ArrowDropDown, Clear, Sync } from "@mui/icons-material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton
} from "@mui/x-data-grid";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAdminSender } from "../api";
import {
  SpotSearchIndexResult,
  SpotSearchQuerySortableField,
  SpotSortableOrder
} from "../generatedApi/adminSearchEngineApi";
import { CancelablePromise } from "../generatedApi/spotManagerApi";
import { useSpotSearch } from "../hooks/useSpotSearch";
import { useSpotItemsQuery } from "../hooks/useSpotItemsQuery";
import { PaginatedDataGrid } from "./PaginatedDataGrid";
import { callbackDraftPathAtom, callbackTabAtom } from "../atom";
import { useAtom } from "jotai/react";
import { useLocation } from "react-router-dom";
import {
  useDebouncedSearch,
  type DraftSearchConditions,
  type DraftSearchLocalState
} from "../hooks/useDebouncedSearch";
import { FieldSelection } from "./GooglePlaceDetailsModal/FieldSelection";
import { SkuSummary } from "./GooglePlaceDetailsModal/SkuSummary";
import { calculateRequiredTiers } from "./GooglePlaceDetailsModal/utils";
import { SelectedFields, DEFAULT_REQUIRED_FIELDS } from "./GooglePlaceDetailsModal/types";
import { BulkOperationResult, hasGooglePlaceId } from "../types/bulkOperations";
import { useBulkGoogleOperations } from "../hooks/useBulkGoogleOperations";

export const SpotItemPageDraftTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchConditions, updateSearchConditions } = useSpotSearch();
  const [, setCallbackDraftPath] = useAtom(callbackDraftPathAtom);
  const [, setCallbackTab] = useAtom(callbackTabAtom);
  const [addMenuAnchorPosition, setAddMenuAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const addMenuOpen = Boolean(addMenuAnchorPosition);

  // デバウンス付きローカル検索状態管理
  const { localState, setters, clearField } = useDebouncedSearch<
    DraftSearchConditions,
    DraftSearchLocalState
  >(searchConditions, updateSearchConditions, 500);

  const {
    data: spotItems,
    loading,
    mutate: reloadSpotItems
  } = useSpotItemsQuery({
    apiName: "admin_search_engine",
    operationId: "searchDraftSpots"
  });

  // スポット詳細に遷移する前に現在の状態をJotaiに保存
  const handleSpotClick = (spotId: string) => {
    setCallbackDraftPath(location.search);
    setCallbackTab("draft");
    navigate(`/spots/${spotId}`);
  };

  const [selectionModel, setSelectionModel] = useState<SpotSearchIndexResult[] | undefined>();

  const [bulkMode, setBulkMode] = useState<
    "publish" | "unpublish" | "googleDetails" | "placeIdRefresh"
  >("publish");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAnchorOrigin, setMenuAnchorOrigin] = useState<{
    vertical: "top" | "center" | "bottom" | number;
    horizontal: "left" | "center" | "right" | number;
  } | null>(null);
  const menuOpen = Boolean(anchorEl) && Boolean(menuAnchorOrigin);
  const bulkMenuHandleClickOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    // ボタンの真下、左端に合わせて表示
    const vertical = rect.bottom;
    const horizontal = rect.left;

    setMenuAnchorOrigin({ vertical, horizontal });
    setAnchorEl(button);
  };
  const bulkMenuHandleClose = () => {
    setAnchorEl(null);
  };
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkHandleClickOpen = () => {
    setBulkOpen(true);
  };
  const bulkHandleClose = () => {
    setBulkOpen(false);
    // Google詳細情報取得のフィールド選択をデフォルトにリセット（単一スポットモーダルとUX一貫性を保つ）
    setSelectedGoogleFields(DEFAULT_REQUIRED_FIELDS);
  };

  const [bulkResult, setBulkResult] = useState<
    Array<{
      spotId: string;
      name: string;
      revisionId: string;
      result: boolean;
    }>
  >([]);

  // Google操作用の詳細な結果
  const [bulkResultDetails, setBulkResultDetails] = useState<BulkOperationResult[]>([]);

  // Google詳細情報取得用のフィールド選択状態
  const [selectedGoogleFields, setSelectedGoogleFields] =
    useState<SelectedFields>(DEFAULT_REQUIRED_FIELDS);

  const [bulkLength, setBulkLength] = useState<number>(0);
  const [bulkSuccessCount, setBulkSuccessCount] = useState<number>(0);
  const [progressValue, setProgressValue] = useState<number | null>(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const progressModalHandleClickOpen = () => {
    setProgressModalOpen(true);
  };
  const progressModalHandleClose = () => {
    setProgressModalOpen(false);
  };

  const { sender: publishSpotRevision } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "default",
    operationId: "publishSpotRevision"
  });

  const { sender: unpublishSpotRevision } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "default",
    operationId: "unpublishSpotRevision"
  });

  // Google一括操作用
  const { performGoogleDetailsUpdate, performPlaceIdRefresh } = useBulkGoogleOperations();

  const onClickBulkPublish = async (mode: "publish" | "unpublish") => {
    if (!selectionModel) return;
    setProgressValue(null);
    setBulkLength(selectionModel.length);
    setBulkSuccessCount(0);
    let successCount = 0;
    let totalBulkResult = bulkResult;
    totalBulkResult = [];
    setBulkResult(totalBulkResult);
    for (let i = 0; i < selectionModel.length; i++) {
      const spotId = selectionModel[i].spot_id;
      const name = selectionModel[i].name;
      const revisionId = selectionModel[i].latest_revision_id;
      if (!spotId || !revisionId) {
        totalBulkResult.unshift({
          spotId,
          name,
          revisionId: "notfound",
          result: false
        });
        totalBulkResult = [...totalBulkResult];
        setBulkResult(totalBulkResult);
        setProgressValue(i + 1);
        continue;
      }

      let res: CancelablePromise<any> | undefined;
      switch (mode) {
        case "publish":
          res = await publishSpotRevision({
            spotId,
            revisionId
          });
          break;
        case "unpublish":
          res = await unpublishSpotRevision({
            spotId,
            revisionId
          });
          break;
      }
      if (res === undefined) {
        totalBulkResult.unshift({
          spotId,
          name,
          revisionId,
          result: false
        });
        totalBulkResult = [...totalBulkResult];
        setBulkResult(totalBulkResult);
        setProgressValue(i + 1);
        continue;
      }
      totalBulkResult.unshift({
        spotId,
        name,
        revisionId,
        result: true
      });
      totalBulkResult = [...totalBulkResult];
      setBulkResult(totalBulkResult);
      setProgressValue(i + 1);
      successCount = successCount + 1;
    }
    setBulkSuccessCount(successCount);
  };

  // Google詳細情報一括取得
  const onClickBulkGoogleDetails = async () => {
    if (!selectionModel) return;

    setProgressValue(null);
    setBulkLength(selectionModel.length);
    setBulkSuccessCount(0);
    let successCount = 0;
    const tempResults: BulkOperationResult[] = [];

    for (let i = 0; i < selectionModel.length; i++) {
      const spot = selectionModel[i];

      let result: BulkOperationResult;
      if (!hasGooglePlaceId(spot)) {
        result = {
          spotId: spot.spot_id,
          name: spot.name,
          status: "skip",
          message: "google_place_idなし"
        };
      } else {
        result = await performGoogleDetailsUpdate(spot, selectedGoogleFields);
      }

      if (result.status === "success") successCount++;
      tempResults.unshift(result);
      setBulkResultDetails([...tempResults]);
      setProgressValue(i + 1);
    }

    setBulkSuccessCount(successCount);
  };

  // PlaceID一括更新
  const onClickBulkPlaceIdRefresh = async () => {
    if (!selectionModel) return;

    setProgressValue(null);
    setBulkLength(selectionModel.length);
    setBulkSuccessCount(0);
    let successCount = 0;
    const tempResults: BulkOperationResult[] = [];

    for (let i = 0; i < selectionModel.length; i++) {
      const spot = selectionModel[i];

      let result: BulkOperationResult;
      if (!hasGooglePlaceId(spot)) {
        result = {
          spotId: spot.spot_id,
          name: spot.name,
          status: "skip",
          message: "google_place_idなし"
        };
      } else {
        result = await performPlaceIdRefresh(spot);
      }

      if (result.status === "success") successCount++;
      tempResults.unshift(result);
      setBulkResultDetails([...tempResults]);
      setProgressValue(i + 1);
    }

    setBulkSuccessCount(successCount);
  };

  return (
    <Stack height="100%" width="80vw" direction="row" spacing={2}>
      <Stack width={300} py={1} spacing={1} sx={{ flexShrink: 0 }}>
        <Stack spacing={1}>
          <Typography variant="body1" fontWeight="bold">
            検索
          </Typography>
        </Stack>
        <TextField
          variant="filled"
          label="スポット名"
          value={localState.nameInput}
          onChange={(e) => setters.setNameInput(e.target.value)}
        />
        <TextField
          variant="filled"
          label="読み仮名"
          value={localState.phoneticNameInput}
          onChange={(e) => setters.setPhoneticNameInput(e.target.value)}
        />
        <TextField
          variant="filled"
          label="緯度"
          value={localState.latInput}
          onChange={(e) => setters.setLatInput(e.target.value)}
          type="number"
        />
        <TextField
          variant="filled"
          label="経度"
          value={localState.lonInput}
          onChange={(e) => setters.setLonInput(e.target.value)}
          type="number"
        />
        <InputLabel id="sort-field-label">ソート名</InputLabel>
        <Select
          labelId="sort-field-label"
          label="ソート名"
          variant="filled"
          value={localState.sortFieldInput}
          onChange={(e) => setters.setSortFieldInput(e.target.value)}
          endAdornment={
            <>
              <IconButton
                sx={{ display: localState.sortFieldInput ? "" : "none" }}
                onClick={() => clearField("sortFieldInput")}
              >
                <Clear />
              </IconButton>
            </>
          }
        >
          <MenuItem value={SpotSearchQuerySortableField.TIMESTAMP_CREATED}>作成日時</MenuItem>
          <MenuItem value={SpotSearchQuerySortableField.DISTANCE}>距離</MenuItem>
        </Select>
        <InputLabel id="sort-order-label">ソート順</InputLabel>
        <Select
          labelId="sort-order-label"
          label="ソート順"
          variant="filled"
          value={localState.sortOrderInput}
          onChange={(e) => setters.setSortOrderInput(e.target.value)}
          endAdornment={
            <>
              <IconButton
                sx={{ display: localState.sortOrderInput ? "" : "none" }}
                onClick={() => clearField("sortOrderInput")}
              >
                <Clear />
              </IconButton>
            </>
          }
        >
          <MenuItem value={SpotSortableOrder.ASC}>昇順</MenuItem>
          <MenuItem value={SpotSortableOrder.DESC}>降順</MenuItem>
        </Select>
        <InputLabel id="published-revision-label">公開状態</InputLabel>
        <Select
          labelId="published-revision-label"
          label="公開状態"
          variant="filled"
          value={localState.publishedRevisionInput}
          onChange={(e) => setters.setPublishedRevisionInput(e.target.value)}
          endAdornment={
            <>
              <IconButton
                sx={{ display: localState.publishedRevisionInput !== "" ? "" : "none" }}
                onClick={() => clearField("publishedRevisionInput")}
              >
                <Clear />
              </IconButton>
            </>
          }
        >
          <MenuItem value={"true"}>公開中</MenuItem>
          <MenuItem value={"false"}>非公開</MenuItem>
        </Select>
        <InputLabel id="has-unpublished-changes-filter-label">未公開の更新</InputLabel>
        <Select
          labelId="has-unpublished-changes-filter-label"
          label="未公開の更新"
          variant="filled"
          value={localState.hasUnpublishedChangesInput}
          onChange={(e) => setters.setHasUnpublishedChangesInput(e.target.value)}
          endAdornment={
            <>
              <IconButton
                sx={{
                  display: localState.hasUnpublishedChangesInput !== "" ? "" : "none"
                }}
                onClick={() => clearField("hasUnpublishedChangesInput")}
              >
                <Clear />
              </IconButton>
            </>
          }
        >
          <MenuItem value={"true"}>未公開の更新有り</MenuItem>
          <MenuItem value={"false"}>最新を公開済み</MenuItem>
        </Select>
      </Stack>
      <Box sx={{ width: "100%", height: "100%", py: 1, minWidth: 0 }}>
        <PaginatedDataGrid
          loading={loading}
          sx={{
            bgcolor: "#fff",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden"
          }}
          rows={spotItems?.results ?? []}
          onRowSelectionModelChange={(rowSelectionModel) => {
            const selectedRowId = new Set(rowSelectionModel);
            const selectedRows = spotItems?.results.filter((dataGridRow) =>
              selectedRowId.has(dataGridRow.spot_id)
            );
            setSelectionModel(selectedRows);
          }}
          rowCount={spotItems?.total ?? 0}
          disableColumnFilter
          pageSizeOptions={[5, 10, 20, 100]}
          columns={[
            {
              field: "spot_id",
              headerName: "スポットID",
              width: 200,
              minWidth: 200,
              flex: 0,
              renderCell: (params) => (
                <span
                  style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => handleSpotClick(params.value)}
                >
                  {params.value}
                </span>
              ),
              sortable: false
            },
            {
              field: "name",
              headerName: "スポット名",
              minWidth: 200,
              flex: 1,
              sortable: false
            },
            {
              width: 200,
              minWidth: 150,
              flex: 0,
              field: "published_revision_id",
              headerName: "公開状態",
              renderCell: (params) => (params.value ? `🟢 公開中(${params.value})` : "🔴 非公開"),
              sortable: false
            },
            {
              field: "latest_revision_id",
              headerName: "最新リビジョンID",
              minWidth: 150,
              flex: 1,
              renderCell: (params) => (params.value ? params.value : "未作成"),
              sortable: false
            },
            {
              field: "has_unpublished_changes",
              headerName: "未公開の更新",
              width: 180,
              minWidth: 150,
              flex: 0,
              renderCell: (params) =>
                !params.row.latest_revision_id
                  ? "-"
                  : params.value
                    ? "⚠️ 未公開の更新あり"
                    : "✅ 最新を公開済み",
              sortable: false
            }
          ]}
          hideFooterSelectedRowCount
          getRowId={(row) => row.spot_id}
          checkboxSelection
          slots={{
            toolbar: () => (
              <GridToolbarContainer sx={{ display: "flex", justifyContent: "space-between" }}>
                <Stack direction="row">
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
                  <GridToolbarExport />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    endIcon={<ArrowDropDown />}
                    disabled={selectionModel?.length === 0 || selectionModel === undefined}
                    onClick={bulkMenuHandleClickOpen}
                  >
                    一括操作
                  </Button>
                  {/* NOTE: DataGrid v7のツールバー内では再レンダリングが頻繁に発生するため、
                      Menuが一瞬左上(0,0)に表示される既知の問題があります。
                      原因: DataGrid内部の状態変更によりanchorEl参照が失効し、
                      Menuの位置が初期値にリセットされる。
                      現在の回避策: アドホック対応として、数値ベースのanchorOriginを使用して座標を直接指定。 */}
                  <Menu
                    id="bulk-menu"
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={() => bulkMenuHandleClose()}
                    anchorOrigin={menuAnchorOrigin || { vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left"
                    }}
                  >
                    <List sx={{ minWidth: 150, p: 0 }}>
                      <ListItemButton
                        onClick={() => {
                          setBulkMode("publish");
                          bulkMenuHandleClose();
                          bulkHandleClickOpen();
                        }}
                      >
                        <ListItemText primary="一括公開" />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => {
                          setBulkMode("unpublish");
                          bulkMenuHandleClose();
                          bulkHandleClickOpen();
                        }}
                      >
                        <ListItemText primary="一括非公開" />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => {
                          setBulkMode("googleDetails");
                          bulkMenuHandleClose();
                          bulkHandleClickOpen();
                        }}
                      >
                        <ListItemText primary="Google詳細情報一括取得" />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => {
                          setBulkMode("placeIdRefresh");
                          bulkMenuHandleClose();
                          bulkHandleClickOpen();
                        }}
                      >
                        <ListItemText primary="PlaceID一括更新" />
                      </ListItemButton>
                    </List>
                  </Menu>
                  <Button
                    variant="contained"
                    size="small"
                    color="inherit"
                    startIcon={<Sync />}
                    onClick={() => reloadSpotItems()}
                  >
                    更新
                  </Button>
                  <ButtonGroup variant="contained" size="small">
                    <Button startIcon={<Add />} onClick={() => navigate("/spots/create")}>
                      追加
                    </Button>
                    <Button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setAddMenuAnchorPosition({
                          top: rect.bottom,
                          left: rect.left
                        });
                      }}
                      aria-label="追加メニューを開く"
                      aria-haspopup="true"
                      aria-expanded={addMenuOpen}
                    >
                      <ArrowDropDown />
                    </Button>
                  </ButtonGroup>
                </Stack>
              </GridToolbarContainer>
            )
          }}
        />
        <Menu
          open={addMenuOpen}
          onClose={() => setAddMenuAnchorPosition(null)}
          anchorReference="anchorPosition"
          anchorPosition={addMenuAnchorPosition ?? undefined}
        >
          <MenuItem
            onClick={() => {
              setAddMenuAnchorPosition(null);
              navigate("/spots/create");
            }}
          >
            地図検索から追加
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAddMenuAnchorPosition(null);
              navigate("/spots/create/url");
            }}
          >
            URLで追加
          </MenuItem>
        </Menu>
        {/* 一括公開/非公開の確認ダイアログ */}
        <Dialog
          open={bulkOpen && (bulkMode === "publish" || bulkMode === "unpublish")}
          onClose={bulkHandleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="alert-dialog-title">
            {bulkMode === "publish" ? "一括公開しますか?" : "一括非公開しますか?"}
            <br />
            <Typography pl={1} variant="body1">
              対象スポット数: {selectionModel?.length ?? 0}
              <br />
              対象一覧は以下の通りです。
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectionModel?.map((row) => (
              <Typography key={row.spot_id} pl={1}>
                {row.spot_id}: {row.name}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={bulkHandleClose} variant="outlined">
              キャンセル
            </Button>
            <Button
              onClick={() => {
                onClickBulkPublish(bulkMode as "publish" | "unpublish");
                progressModalHandleClickOpen();
                bulkHandleClose();
              }}
              variant="contained"
              autoFocus
            >
              {bulkMode === "publish" ? "一括公開" : "一括非公開"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Google詳細情報一括取得の確認ダイアログ */}
        <Dialog
          open={bulkOpen && bulkMode === "googleDetails"}
          onClose={bulkHandleClose}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Google詳細情報を一括取得しますか?
            <Typography pl={1} variant="body1">
              対象スポット数: {selectionModel?.length ?? 0}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <FieldSelection
              selectedFields={selectedGoogleFields}
              onChange={(field, checked) => {
                // NOTE: FieldSelectionコンポーネントは連動フィールド（formattedAddress+addressComponents、rating+userRatingCount）
                // について、onChangeを2回呼び出すため、このハンドラーは1フィールドずつ更新する形で問題ない
                setSelectedGoogleFields((prev) => ({ ...prev, [field]: checked }));
              }}
            />
            <SkuSummary selectedFields={selectedGoogleFields} />

            <Typography variant="subtitle2" mt={2}>
              対象スポット:
            </Typography>
            {selectionModel?.map((row) => (
              <Typography key={row.spot_id} pl={1} variant="body2">
                {row.spot_id}: {row.name}
                {!hasGooglePlaceId(row) && (
                  <Chip label="PlaceIDなし" size="small" color="warning" sx={{ ml: 1 }} />
                )}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={bulkHandleClose} variant="outlined">
              キャンセル
            </Button>
            <Button
              onClick={() => {
                // フィールド未選択チェック（単一スポットモーダルと同様のバリデーション）
                const tiers = calculateRequiredTiers(selectedGoogleFields);
                if (tiers.length === 0) {
                  alert("少なくとも1つのフィールドを選択してください");
                  return;
                }
                onClickBulkGoogleDetails();
                progressModalHandleClickOpen();
                bulkHandleClose();
              }}
              variant="contained"
            >
              取得
            </Button>
          </DialogActions>
        </Dialog>

        {/* PlaceID一括更新の確認ダイアログ */}
        <Dialog
          open={bulkOpen && bulkMode === "placeIdRefresh"}
          onClose={bulkHandleClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            PlaceIDを一括更新しますか?
            <Typography pl={1} variant="body1">
              対象スポット数: {selectionModel?.length ?? 0}
            </Typography>
            <Alert severity="warning" sx={{ mt: 1 }}>
              Google側でplace_idが変更されている場合に更新されます
            </Alert>
          </DialogTitle>
          <DialogContent>
            {selectionModel?.map((row) => (
              <Typography key={row.spot_id} pl={1} variant="body2">
                {row.spot_id}: {row.name}
                {!hasGooglePlaceId(row) && (
                  <Chip label="PlaceIDなし" size="small" color="warning" sx={{ ml: 1 }} />
                )}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={bulkHandleClose} variant="outlined">
              キャンセル
            </Button>
            <Button
              onClick={() => {
                onClickBulkPlaceIdRefresh();
                progressModalHandleClickOpen();
                bulkHandleClose();
              }}
              variant="contained"
            >
              実行
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={progressModalOpen}
          aria-labelledby="progres-dialog-title"
          aria-describedby="progress-dialog-description"
          fullWidth
          maxWidth="sm"
          sx={{ maxHeight: "80%" }}
        >
          <DialogTitle id="progress-dialog-title">
            {progressValue === bulkLength ? (
              <Typography fontSize="body1">
                {(bulkMode === "publish" || bulkMode === "unpublish") &&
                  `完了(${bulkLength}件中 ${bulkSuccessCount}件 成功)`}
                {(bulkMode === "googleDetails" || bulkMode === "placeIdRefresh") &&
                  (() => {
                    const successCount = bulkResultDetails.filter(
                      (r) => r.status === "success"
                    ).length;
                    const unchangedCount = bulkResultDetails.filter(
                      (r) => r.status === "unchanged"
                    ).length;
                    const skipCount = bulkResultDetails.filter((r) => r.status === "skip").length;
                    const notFoundCount = bulkResultDetails.filter(
                      (r) => r.status === "not_found"
                    ).length;
                    const errorCount = bulkResultDetails.filter((r) => r.status === "error").length;

                    const parts = [];
                    if (successCount > 0)
                      parts.push(
                        `${bulkMode === "googleDetails" ? "取得" : "更新"}${successCount}件`
                      );
                    if (unchangedCount > 0) parts.push(`変更なし${unchangedCount}件`);
                    if (skipCount > 0) parts.push(`スキップ${skipCount}件`);
                    if (notFoundCount > 0) parts.push(`無効${notFoundCount}件`);
                    if (errorCount > 0) parts.push(`失敗${errorCount}件`);

                    return `完了(${bulkLength}件中: ${parts.join("、")})`;
                  })()}
              </Typography>
            ) : (
              <Typography fontSize="body1">
                {bulkMode === "publish" && "一括公開中..."}
                {bulkMode === "unpublish" && "一括非公開中..."}
                {bulkMode === "googleDetails" && "Google詳細情報一括取得中..."}
                {bulkMode === "placeIdRefresh" && "PlaceID一括更新中..."}
              </Typography>
            )}
            <Stack>
              {progressValue && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(progressValue / bulkLength) * 100}
                    />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">{`${Math.round(
                      (progressValue / bulkLength) * 100
                    )}%`}</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </DialogTitle>
          <DialogContent>
            {/* 既存の一括公開/非公開用 */}
            {(bulkMode === "publish" || bulkMode === "unpublish") &&
              bulkResult.map((result) => (
                <Box key={result.spotId} p={1}>
                  <Alert severity={result.result ? "success" : "error"}>
                    {result.result ? "成功" : "失敗"}{" "}
                    <Link to={`/spots/${result.spotId}`} target="_blank">
                      {result.spotId}
                    </Link>
                    : {result.name}
                  </Alert>
                </Box>
              ))}

            {/* 新規のGoogle操作用 */}
            {(bulkMode === "googleDetails" || bulkMode === "placeIdRefresh") &&
              bulkResultDetails.map((result) => (
                <Box key={result.spotId} p={1}>
                  <Alert
                    severity={
                      result.status === "success"
                        ? "success"
                        : result.status === "skip" || result.status === "unchanged"
                          ? "info"
                          : result.status === "not_found"
                            ? "warning"
                            : "error"
                    }
                  >
                    {result.status === "success" && "✅ 成功"}
                    {result.status === "skip" && "⚠️ スキップ"}
                    {result.status === "unchanged" && "✅ 変更なし"}
                    {result.status === "not_found" && "⚠️ 無効"}
                    {result.status === "error" && "❌ 失敗"}{" "}
                    <Link to={`/spots/${result.spotId}`} target="_blank">
                      {result.spotId}
                    </Link>
                    : {result.name}
                    {result.message && ` - ${result.message}`}
                  </Alert>
                </Box>
              ))}
          </DialogContent>
          <DialogActions>
            <Stack direction="row" justifyContent="space-between" width="100%">
              {progressValue !== bulkLength ? (
                <Typography pl={4} fontSize="body1">
                  処理が完了するまで閉じないでください。
                </Typography>
              ) : (
                <Box></Box>
              )}
              <Button
                onClick={() => {
                  reloadSpotItems();
                  progressModalHandleClose();
                  // 状態リセット（古いデータが残らないようにする）
                  setBulkResultDetails([]);
                  setBulkResult([]);
                }}
                variant="outlined"
              >
                閉じる
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      </Box>
    </Stack>
  );
};
